import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  FileSignature,
  Plus,
  Trash2,
  Send,
  AlertCircle,
  Loader2,
  User,
  Mail,
  Upload,
  FileText,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { signingService } from '../../services/api';
import useModal from '../../hooks/useModal';
import useTemplateEditor from './template-editor/hooks/useTemplateEditor';
import useFieldPlacement from './template-editor/hooks/useFieldPlacement';
import PDFViewer from './template-editor/PDFViewer';
import FieldPalette from './template-editor/FieldPalette';
import FieldOverlay from './template-editor/FieldOverlay';
import SubmitterRolesManager from './template-editor/SubmitterRolesManager';
import PageNavigator from './template-editor/PageNavigator';

const SendDocumentModal = ({ isOpen, onClose, onSuccess, caseId, clientId }) => {
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mode, setMode] = useState('template'); // 'template' or 'upload'
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  // Upload wizard state
  const [uploadStep, setUploadStep] = useState(1); // 1=upload, 2=fields, 3=signers
  const [isDragOver, setIsDragOver] = useState(false);
  const lastLoadedFileRef = useRef(null);

  // Template editor hooks (used in upload step 2)
  const editor = useTemplateEditor();
  const placement = useFieldPlacement({
    pageDimensions: editor.pageDimensions,
    onAddField: editor.addField,
    onMoveField: editor.moveField,
    onResizeField: editor.resizeField,
    onSelectField: editor.selectField,
  });

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  // Form data
  const [formData, setFormData] = useState({
    template_id: '',
    name: '',
    subject: '',
    message: '',
    send_email: true,
    signers: [{ email: '', name: '', role: 'Signer' }]
  });

  // Get available roles from selected template
  const templateRoles = selectedTemplate?.submitters?.map(s => s.name) || [];

  // Mouse listeners for field drag/resize (step 2)
  useEffect(() => {
    if (!placement.isDragging && !placement.isResizing) return;
    const handleMouseMove = (e) => placement.handleMouseMove(e);
    const handleMouseUp = () => placement.handleMouseUp();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [placement.isDragging, placement.isResizing, placement]);

  // Keyboard shortcuts for field editing (step 2)
  const { selectedFieldId, deleteField, deselectField } = editor;
  useEffect(() => {
    if (mode !== 'upload' || uploadStep !== 2) return;
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        deleteField(selectedFieldId);
      }
      if (e.key === 'Escape') {
        deselectField();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, uploadStep, selectedFieldId, deleteField, deselectField]);

  // Keep a stable ref to editor.reset to avoid dep issues
  const editorResetRef = useRef(editor.reset);
  editorResetRef.current = editor.reset;

  // Fetch templates and reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    } else {
      setFormData({
        template_id: '',
        name: '',
        subject: '',
        message: '',
        send_email: true,
        signers: [{ email: '', name: '', role: 'Signer' }]
      });
      setSelectedTemplate(null);
      setError('');
      setMode('template');
      setUploadFile(null);
      setUploadStep(1);
      setIsDragOver(false);
      lastLoadedFileRef.current = null;
      editorResetRef.current();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await signingService.getTemplates();
      setTemplates(response.data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load document templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'template_id') {
      const template = templates.find(t => String(t.id) === value);
      setSelectedTemplate(template);

      if (template?.submitters?.length > 0) {
        const signersFromTemplate = template.submitters.map(submitter => ({
          email: '',
          name: '',
          role: submitter.name
        }));
        setFormData(prev => ({
          ...prev,
          template_id: value,
          signers: signersFromTemplate
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSignerChange = (index, field, value) => {
    setFormData(prev => {
      const newSigners = [...prev.signers];
      newSigners[index] = { ...newSigners[index], [field]: value };
      return { ...prev, signers: newSigners };
    });
  };

  const addSigner = () => {
    if (mode === 'upload' && uploadStep === 3) {
      // In upload step 3, add signer with first available role
      const usedRoles = formData.signers.map(s => s.role);
      const availableRole = editor.roles.find(r => !usedRoles.includes(r.name));
      setFormData(prev => ({
        ...prev,
        signers: [...prev.signers, { email: '', name: '', role: availableRole?.name || editor.roles[0]?.name || 'Signer' }]
      }));
    } else {
      const defaultRole = mode === 'template' && templateRoles.length > 0 ? templateRoles[0] : 'Signer';
      setFormData(prev => ({
        ...prev,
        signers: [...prev.signers, { email: '', name: '', role: defaultRole }]
      }));
    }
  };

  const removeSigner = (index) => {
    if (formData.signers.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      signers: prev.signers.filter((_, i) => i !== index)
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are supported for signing');
        return;
      }
      setUploadFile(file);
      setError('');
    }
  };

  // Drag-and-drop handlers for file upload
  const handleFileDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleFileDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.type === 'application/pdf') {
      setUploadFile(file);
      setError('');
    } else if (file) {
      setError('Only PDF files are supported for signing');
    }
  }, []);

  // Upload step navigation
  const handleProceedToFields = useCallback(() => {
    if (!uploadFile) return;
    if (uploadFile !== lastLoadedFileRef.current) {
      editor.loadPDF(uploadFile);
      lastLoadedFileRef.current = uploadFile;
    }
    setUploadStep(2);
  }, [uploadFile, editor]);

  const handleProceedToSigners = useCallback(() => {
    if (editor.fields.length === 0) {
      setError('Place at least one signature field on the document before continuing');
      return;
    }
    setError('');
    // Create signer entries from roles, preserving any previously entered info
    const existingSigners = formData.signers;
    const newSigners = editor.roles.map(role => {
      const existing = existingSigners.find(s => s.role === role.name);
      return existing || { email: '', name: '', role: role.name };
    });
    setFormData(prev => ({ ...prev, signers: newSigners }));
    setUploadStep(3);
  }, [editor.fields.length, editor.roles, formData.signers]);

  // Field drop on PDF viewer
  const handleFieldDrop = useCallback((e, containerRect) => {
    placement.handleCanvasDrop(e, containerRect);
  }, [placement]);

  // Mode switch handler
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setError('');
    if (newMode === 'template') {
      setUploadStep(1);
      setUploadFile(null);
      lastLoadedFileRef.current = null;
      editor.reset();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    // Validate signers
    const invalidSigners = formData.signers.filter(s => !s.email || !s.name);
    if (invalidSigners.length > 0) {
      setError('All signers must have an email and name');
      setLoading(false);
      return;
    }

    try {
      if (mode === 'upload') {
        if (!uploadFile) {
          setError('Please select a PDF to upload');
          setLoading(false);
          return;
        }

        // Build fields with areas from editor
        const fieldsData = editor.fields.map(f => {
          const role = editor.roles.find(r => r.uuid === f.roleUuid);
          return {
            name: f.name,
            type: f.type,
            role: role?.name || 'Signer',
            required: f.required,
            areas: f.areas.map(a => ({ x: a.x, y: a.y, w: a.w, h: a.h, page: a.page })),
          };
        });

        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('signers_json', JSON.stringify(formData.signers));
        fd.append('fields_json', JSON.stringify(fieldsData));
        fd.append('name', formData.name || uploadFile.name.replace(/\.pdf$/i, ''));
        fd.append('subject', formData.subject);
        fd.append('message', formData.message);
        fd.append('send_email', formData.send_email);
        if (caseId) fd.append('case_id', caseId);
        if (clientId) fd.append('client_id', clientId);

        const response = await signingService.createEnvelopeFromUpload(fd);
        if (onSuccess) onSuccess(response.data);
      } else {
        // Template mode — existing flow
        const selectedTpl = templates.find(t => String(t.id) === formData.template_id);
        const envelopeName = formData.name || selectedTpl?.name || 'Document for Signature';

        const envelopeData = {
          template_id: formData.template_id,
          name: envelopeName,
          subject: formData.subject || `Please sign: ${envelopeName}`,
          message: formData.message,
          send_email: formData.send_email,
          signers: formData.signers,
          case_id: caseId || null,
          client_id: clientId || null
        };

        const response = await signingService.createEnvelope(envelopeData);
        if (onSuccess) onSuccess(response.data);
      }

      // Reset and close
      setFormData({
        template_id: '',
        name: '',
        subject: '',
        message: '',
        send_email: true,
        signers: [{ email: '', name: '', role: 'Signer' }]
      });
      setSelectedTemplate(null);
      setUploadFile(null);
      setUploadStep(1);
      editor.reset();

      onClose();
    } catch (err) {
      console.error('Error sending document:', err);
      setError(err.response?.data?.detail || 'Failed to send document for signature');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isFieldEditorStep = mode === 'upload' && uploadStep === 2;

  // ─── Template mode form validity ───
  const isTemplateFormValid = formData.template_id && formData.signers.length > 0 && formData.signers.every(s => s.email && s.name);
  // ─── Upload step 3 form validity ───
  const isUploadFormValid = uploadFile && formData.signers.length > 0 && formData.signers.every(s => s.email && s.name);

  // ─── Step indicator for upload mode ───
  const StepIndicator = () => (
    <div className="flex items-center gap-1 text-xs">
      {['Upload', 'Place Fields', 'Send'].map((label, i) => {
        const stepNum = i + 1;
        const isActive = uploadStep === stepNum;
        const isDone = uploadStep > stepNum;
        return (
          <React.Fragment key={stepNum}>
            {i > 0 && <div className="w-4 h-px bg-gray-300" />}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${
              isActive ? 'bg-yellow-100 text-yellow-700 font-medium' :
              isDone ? 'bg-green-100 text-green-700' : 'text-gray-400'
            }`}>
              <span className={`w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-bold ${
                isActive ? 'bg-yellow-500 text-white' :
                isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>{stepNum}</span>
              <span className="hidden sm:inline">{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ─── Mode toggle (shown in step 1 and template mode) ───
  const ModeToggle = () => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Document Source</label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleModeSwitch('template')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${
            mode === 'template'
              ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FileText className="w-4 h-4" />
          Use Template
        </button>
        <button
          type="button"
          onClick={() => handleModeSwitch('upload')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${
            mode === 'upload'
              ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Upload className="w-4 h-4" />
          Upload PDF
        </button>
      </div>
    </div>
  );

  // ─── Signer rows (inline JSX to avoid focus loss from component remounting) ───
  const signerRowsJSX = (roleOptions) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">Recipients *</label>
        <button
          type="button"
          onClick={addSigner}
          className="flex items-center gap-1 text-sm text-yellow-600 hover:text-yellow-700"
        >
          <Plus className="w-4 h-4" />
          Add Recipient
        </button>
      </div>

      <div className="space-y-3">
        {formData.signers.map((signer, index) => (
          <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={signer.email}
                    onChange={(e) => handleSignerChange(index, 'email', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="Email address"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={signer.name}
                    onChange={(e) => handleSignerChange(index, 'name', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="Full name"
                  />
                </div>
              </div>
              <div>
                {roleOptions ? (
                  <select
                    value={signer.role}
                    onChange={(e) => handleSignerChange(index, 'role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  >
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={signer.role}
                    onChange={(e) => handleSignerChange(index, 'role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="Role (e.g., Client)"
                  />
                )}
              </div>
            </div>
            {formData.signers.length > 1 && (
              <button
                type="button"
                onClick={() => removeSigner(index)}
                className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // ─── Email options (inline JSX to avoid focus loss from component remounting) ───
  const emailOptionsJSX = (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-3">Email Options</h3>
      <div className="space-y-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.send_email}
            onChange={(e) => handleChange('send_email', e.target.checked)}
            className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-700">Send signing invitation via email</span>
        </label>

        {formData.send_email && (
          <>
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Email Subject
              </label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Please sign: Document Name"
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message to Recipients
              </label>
              <textarea
                id="message"
                rows={3}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Please review and sign the attached document..."
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      {/* Backdrop */}
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>

      {/* Modal */}
      <div
        className={`bg-white rounded-lg shadow-xl w-full mx-4 flex flex-col transition-all duration-200 ${
          isFieldEditorStep ? 'max-w-7xl h-[90vh]' : 'max-w-2xl max-h-[90vh]'
        }`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileSignature className="w-6 h-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">Send for Signature</h2>
          </div>
          {mode === 'upload' && <StepIndicator />}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Error bar */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-shrink-0">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* ────────────────── TEMPLATE MODE ────────────────── */}
        {mode === 'template' && (
          <>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              <ModeToggle />

              {/* Template Selection */}
              <div>
                <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
                  Document Template *
                </label>
                {loadingTemplates ? (
                  <div className="flex items-center gap-2 text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading templates...
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2">
                    No templates available. Try uploading a PDF instead.
                  </div>
                ) : (
                  <select
                    id="template"
                    value={formData.template_id}
                    onChange={(e) => handleChange('template_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">Select a template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Envelope Name */}
              <div>
                <label htmlFor="tpl-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Envelope Name
                </label>
                <input
                  type="text"
                  id="tpl-name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Client Retainer Agreement"
                />
                <p className="mt-1 text-xs text-gray-500">Leave blank to use the template name</p>
              </div>

              {signerRowsJSX(templateRoles.length > 0 ? templateRoles : null)}

              {emailOptionsJSX}
            </div>

            {/* Template mode footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !isTemplateFormValid || loadingTemplates}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send for Signature</>
                )}
              </button>
            </div>
          </>
        )}

        {/* ────────────────── UPLOAD MODE — STEP 1: Upload File ────────────────── */}
        {mode === 'upload' && uploadStep === 1 && (
          <>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              <ModeToggle />

              {/* File Upload with Drag-and-Drop */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PDF Document *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {uploadFile ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-gray-900 truncate">{uploadFile.name}</span>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        ({(uploadFile.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setUploadFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="text-gray-400 hover:text-red-500 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onDragOver={handleFileDragOver}
                    onDragEnter={handleFileDragOver}
                    onDragLeave={handleFileDragLeave}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-full p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                      isDragOver
                        ? 'border-yellow-400 bg-yellow-50'
                        : 'border-gray-300 hover:border-yellow-400 hover:bg-yellow-50'
                    }`}
                  >
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {isDragOver ? 'Drop PDF here' : 'Drag & drop a PDF or click to select'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">You&apos;ll place signature fields in the next step</p>
                  </div>
                )}
              </div>

              {/* Envelope Name */}
              <div>
                <label htmlFor="upload-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Envelope Name
                </label>
                <input
                  type="text"
                  id="upload-name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Settlement Agreement"
                />
                <p className="mt-1 text-xs text-gray-500">Leave blank to use the file name</p>
              </div>
            </div>

            {/* Step 1 footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProceedToFields}
                disabled={!uploadFile}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </>
        )}

        {/* ────────────────── UPLOAD MODE — STEP 2: Place Fields ────────────────── */}
        {isFieldEditorStep && (
          <>
            <div className="flex-1 flex overflow-hidden">
              {/* Left sidebar */}
              <div className="w-64 flex-shrink-0 p-4 space-y-4 overflow-y-auto border-r border-gray-200 bg-gray-50">
                <FieldPalette
                  onDragStart={placement.handlePaletteDragStart}
                  selectedToolType={placement.selectedToolType}
                  onToolSelect={placement.setSelectedToolType}
                  disabled={!editor.pdfFile}
                />
                <SubmitterRolesManager
                  roles={editor.roles}
                  activeRoleUuid={editor.activeRoleUuid}
                  onSetActiveRole={editor.setActiveRoleUuid}
                  onAddRole={editor.addRole}
                  onUpdateRole={editor.updateRole}
                  onRemoveRole={editor.removeRole}
                />

                {/* Selected field info */}
                {editor.selectedField && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{editor.selectedField.name}</span>
                      <button
                        onClick={() => editor.deleteField(editor.selectedFieldId)}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Delete field"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Assigned to: {editor.roles.find(r => r.uuid === editor.selectedField.roleUuid)?.name || 'Unknown'}
                    </p>
                  </div>
                )}

                {/* Field count */}
                <div className="p-3 bg-gray-100 rounded-lg text-xs text-gray-600">
                  <div className="flex justify-between">
                    <span>Total fields:</span>
                    <span className="font-medium">{editor.fields.length}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>On this page:</span>
                    <span className="font-medium">{editor.currentPageFields.length}</span>
                  </div>
                </div>
              </div>

              {/* Center: PDF Viewer */}
              <div className="flex-1 flex flex-col overflow-hidden bg-gray-100">
                {editor.pdfFile ? (
                  <>
                    <PDFViewer
                      pdfFile={editor.pdfFile}
                      pdfUrl={editor.pdfUrl}
                      currentPage={editor.currentPage}
                      onDocumentLoad={editor.handleDocumentLoad}
                      onPageLoad={editor.handlePageLoad}
                      onDragOver={placement.handleCanvasDragOver}
                      onDrop={handleFieldDrop}
                      onPageClick={placement.handleCanvasClick}
                      className="flex-1 overflow-auto"
                    >
                      {editor.currentPageFields.map((field) => (
                        <FieldOverlay
                          key={field.uuid}
                          field={field}
                          role={editor.roles.find(r => r.uuid === field.roleUuid)}
                          pageWidth={editor.pageDimensions.width}
                          pageHeight={editor.pageDimensions.height}
                          isSelected={editor.selectedFieldId === field.uuid}
                          isDragging={placement.draggedFieldId === field.uuid && placement.isDragging}
                          onMouseDown={placement.handleFieldMouseDown}
                          onResizeStart={placement.handleFieldMouseDown}
                        />
                      ))}
                    </PDFViewer>

                    <div className="flex-shrink-0 border-t border-gray-200 bg-white">
                      <PageNavigator
                        currentPage={editor.currentPage}
                        totalPages={editor.totalPages}
                        onPreviousPage={editor.goToPreviousPage}
                        onNextPage={editor.goToNextPage}
                        onGoToPage={editor.goToPage}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-gray-300" />
                      <p className="text-sm">Loading PDF...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={() => setUploadStep(1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <p className="text-xs text-gray-500 hidden sm:block">
                Drag fields from the palette onto the PDF, then assign them to signer roles
              </p>
              <button
                type="button"
                onClick={handleProceedToSigners}
                disabled={editor.fields.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                Continue to Signers
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </>
        )}

        {/* ────────────────── UPLOAD MODE — STEP 3: Signers & Send ────────────────── */}
        {mode === 'upload' && uploadStep === 3 && (
          <>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Summary */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <FileText className="w-4 h-4 text-red-500" />
                  <span className="font-medium">{uploadFile?.name}</span>
                  <span className="text-gray-400">|</span>
                  <span>{editor.fields.length} field{editor.fields.length !== 1 ? 's' : ''} placed</span>
                  <span className="text-gray-400">|</span>
                  <span>{editor.roles.length} role{editor.roles.length !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {signerRowsJSX(editor.roles.map(r => r.name))}

              {emailOptionsJSX}
            </div>

            {/* Step 3 footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={() => setUploadStep(2)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Fields
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !isUploadFormValid}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send for Signature</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SendDocumentModal;
